import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { NON_ASCII, useEnglishOnly } from "../../util/englishOnly";

/**
 * 사내 계정 ID 입력칸 — 로그인과 직원 생성/수정이 <b>같은 규칙</b>을 쓰게 하는 공용 입력.
 *
 * 계정 ID 는 <b>로컬파트만</b>이다 — DB 의 employee.email 에는 'ksw' 처럼 @ 앞부분만 들어 있고
 * 서버는 그 값으로 정확히 조회한다(AuthController.login → findByEmail).
 * 그런데 사람들이 습관적으로 '@onandme.com' 까지 적어 로그인·계정 생성이 어긋났다.
 * 그래서 도메인은 <b>입력칸 뒤에 회색으로 따라붙여 보여주고</b>, 값에는 애초에 못 들어가게 한다.
 */
export const ACCOUNT_DOMAIN = "@onandme.com";

/** 입력칸 글자 크기 — 실제 input 과 폭 측정용 mirror 가 반드시 같은 값을 써야 한다. */
const ACCOUNT_FONT_SIZE = 14;

/** 입력·붙여넣기로 들어온 '@도메인' 을 벗겨 로컬파트만 남긴다. */
export const stripDomain = (v) => (v || "").replace(/@.*$/, "").trim();

/**
 * 입력값을 서버가 아는 계정명 꼴로 정리한다: 도메인 제거 → 한글 등 비ASCII 제거.
 *
 * <p>영문·숫자·특수문자는 전부 남긴다 — 비밀번호와 같은 규칙이다. 계정명을 좁게 잡으면
 * 사람들이 친 글자가 소리 없이 사라져 "왜 안 써지지" 가 되는데, 실제 계정에 쓰이는 문자를
 * 우리가 다 알고 있지도 않다. 도메인만은 여전히 벗긴다 — 이 컴포넌트가 있는 이유다.
 * 영문은 그대로 통과하므로 몇 번을 돌려도 결과가 같다 — 중복 호출해도 안전하다.
 */
export const toAccount = (v) => stripDomain(v).replace(NON_ASCII, "");

/**
 * @param {string}   value      로컬파트만 담긴 현재 값
 * @param {function} onChange   정리된 값을 문자열로 넘겨준다(한글 조합 중에는 조합 중인 원문 그대로)
 * @param {function} [onKeyDown]
 * @param {boolean}  [autoFocus]
 * @param {boolean}  [disabled]
 */
export default function AccountIdField({ value, onChange, onKeyDown, autoFocus = false, disabled = false }) {
    /* ── '@onandme.com' 을 입력 글자 바로 뒤에 붙이기 ────────────────────────
     * 오른쪽 끝에 고정된 어도먼트는 타이핑한 글자와 멀찍이 떨어져 별개의 안내문처럼 읽힌다.
     * 대신 input 의 폭을 글자 폭만큼만 주고 그 뒤에 도메인을 놓아, 실제로 이어져 보이게 한다.
     * 폭은 같은 글꼴·크기의 숨은 mirror span 을 실측해서 얻는다 — ch 같은 근사 단위는
     * 가변폭 글꼴에서 'iii' 와 'www' 가 크게 어긋난다.
     * ------------------------------------------------------------------ */
    const inputRef  = useRef(null);
    const mirrorRef = useRef(null);
    const [textW, setTextW] = useState(0);
    useLayoutEffect(() => {
        const input = inputRef.current, mirror = mirrorRef.current;
        if (!input || !mirror) return;
        // 글꼴 지표를 추측하지 말고 input 의 실제 계산값을 그대로 복사한다 —
        // 테마 폰트·자간이 바뀌어도 측정이 어긋나지 않는다.
        const cs = window.getComputedStyle(input);
        ["fontFamily", "fontSize", "fontWeight", "fontStyle", "letterSpacing"]
            .forEach(k => { mirror.style[k] = cs[k]; });
        setTextW(mirror.offsetWidth);
    }, [value]);

    /* 한글은 IME 조합 시작 시점에 세션을 끊어 애초에 못 들어오게 하고(imeProps),
     * 그래도 새어 들어온 것(붙여넣기 등)은 값에서 걷어낸다. 걷어냈다는 사실은 notice 로 알려 준다. */
    const { notice, sanitize, clearNotice, imeProps } = useEnglishOnly();

    const commitAccount = (el) => {
        const next = sanitize(stripDomain(el.value));
        // 부모 state 가 그대로면 리렌더가 없어 DOM 에 한글이 남는다 — input 값도 직접 되돌린다.
        if (el.value !== next) el.value = next;
        onChange?.(next);
    };

    const handleChange = (e) => commitAccount(e.target);

    return (
        <Box sx={{ position: "relative" }}>
            {/* 폭 측정용 mirror — 화면에 안 보이지만 실제 input 과 같은 글꼴·크기여야 한다.
                whiteSpace: pre 라야 연속 공백까지 그대로 폭에 반영된다. */}
            <Box
                ref={mirrorRef}
                aria-hidden
                sx={{
                    position: "absolute", visibility: "hidden", pointerEvents: "none",
                    top: 0, left: 0,
                    whiteSpace: "pre",   // 연속 공백까지 폭에 반영
                }}>
                {value}
            </Box>
            <TextField
                size="small"
                fullWidth
                value={value}
                inputRef={inputRef}
                disabled={disabled}
                // 붙여넣기·습관으로 도메인을 같이 적어도 입력칸에는 영문 로컬파트만 남는다.
                onChange={handleChange}
                onKeyDown={onKeyDown}
                error={!!notice}
                helperText={notice}
                inputProps={{
                    inputMode: "email",
                    ...imeProps,
                    // 조합이 안 끝난 채 버튼을 눌러 빠져나가는 경우까지 정리.
                    onBlur: (e) => commitAccount(e.target),
                    onFocus: clearNotice,
                }}
                autoFocus={autoFocus}
                // 도메인 오른쪽 빈 칸을 눌러도 입력칸이 잡히게 — 칸 전체가 하나로 보이므로.
                onClick={() => inputRef.current?.focus()}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="start" sx={{ ml: 0, mr: "auto" }}>
                            <Typography sx={{
                                fontSize: ACCOUNT_FONT_SIZE,
                                userSelect: "none", whiteSpace: "nowrap",
                            }}>
                                {ACCOUNT_DOMAIN}
                            </Typography>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    // input 을 글자 폭만큼만 — 그래야 뒤따르는 도메인이 글자에 딱 붙는다.
                    // 남은 자리는 도메인이 mr:auto 로 왼쪽에 붙어 있게 두고 비워 둔다.
                    "& .MuiInputBase-input": {
                        fontSize: ACCOUNT_FONT_SIZE,
                        flex: "0 0 auto",
                        width: `${textW}px`,
                        minWidth: 2,
                        pr: 0,
                    },
                }}
            />
        </Box>
    );
}
